import { createStore } from 'zustand/vanilla'
import type { VehiclePosition } from './transit'
import { useTripPlanStore } from './tripPlan'
import { sameRouteFamily } from '../ui/colors'
import type { BusLeg, Itinerary, SubwayLeg } from '../services/api'

// The ride session: what the rider is on, where they get off, and — when the ride came
// from a planned itinerary — which leg of that plan they're currently living through.
//
// A planned trip with a transfer is a sequence of ride legs, and the rider is only ever
// bound to a vehicle for one of them at a time. `phase` tracks that:
//
//   idle ──startPlannedTrip──> awaiting_board ──startRide──> riding
//                                    ▲                          │
//                                    └──── advanceLeg ──────────┘ (mid-trip: transfer)
//                                                                └──> idle (final leg)
//
// An unplanned ride (tap a train on the map, no itinerary) skips straight to `riding`
// with no armed leg; the rider picks their alight stop by tapping it in the RiderPanel.

export type RidePhase = 'idle' | 'awaiting_board' | 'riding' | 'transfer'

export type RideLeg = SubwayLeg | BusLeg

export interface Stop {
  stopId: string
  stopName: string
  stopSequence: number
  arrivalTime: number | null
  departureTime: number | null
  arrivalDelay?: number | null
  accessible?: boolean
}

/** Trip-update stop ids are platform ids ("R14N"), which some feeds prefix with an agency
 *  tag the static bundle lacks. Same normalization as backend realtime.ts. */
function normalizeStopId(id: string): string {
  return id.replace(/^MTA[_ ]?/, '')
}

function sameStop(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  return normalizeStopId(a) === normalizeStopId(b)
}

type DestinationProgress = Pick<
  RiderState,
  'destinationStopId' | 'stopsUntilDestination' | 'upcomingStops' | 'currentStopIndex'
>

/** Zero also means "no destination" and "stops have not loaded", so consumers must use
 * this predicate instead of treating the raw countdown as an arrival signal. */
export function hasReachedDestination(progress: DestinationProgress): boolean {
  if (!progress.destinationStopId || progress.stopsUntilDestination !== 0) return false
  return sameStop(
    progress.upcomingStops[progress.currentStopIndex]?.stopId,
    progress.destinationStopId,
  )
}

export function alightHeadline(progress: DestinationProgress): string | null {
  if (!progress.destinationStopId) return null
  if (hasReachedDestination(progress)) return 'GET OFF AT THIS STOP'
  if (progress.stopsUntilDestination === 1) return 'GET OFF AT THE NEXT STOP'
  if (progress.stopsUntilDestination > 1) {
    return `${progress.stopsUntilDestination} STOPS UNTIL YOU GET OFF`
  }
  return null
}

/** Where the rider gets off this leg. Trip updates speak PLATFORM ids, while leg.stops[]
 *  speaks station ids — only `to.platformId` is in the right namespace. */
export function legDestinationStopId(leg: RideLeg): string | null {
  return leg.to.platformId ?? leg.to.stationId ?? null
}

function rideLegs(it: Itinerary): number[] {
  const out: number[] = []
  it.legs.forEach((l, i) => { if (l.mode !== 'walk') out.push(i) })
  return out
}

/** The itinerary the session is following, or null for an unplanned ride. */
function activeItinerary(): Itinerary | null {
  return useTripPlanStore.getState().activeItinerary
}

/**
 * Drop the planned trip when the session following it ends. The itinerary drives the map's
 * TRIP-FOCUS mode — only its lines are drawn — so leaving it set strands the map on three
 * routes, and any vehicle boarded afterwards is filtered off it.
 */
function clearPlan(): void {
  if (useTripPlanStore.getState().activeItinerary) {
    useTripPlanStore.getState().setActiveItinerary(null)
  }
}

interface RiderState {
  phase: RidePhase
  /** True while bound to a vehicle. Kept as its own flag: callers (map, websocket) ask
   *  "is the rider on a vehicle right now", which is narrower than "a session exists". */
  isRiding: boolean
  trackedVehicleId: string | null
  trackedVehicle: VehiclePosition | null
  upcomingStops: Stop[]
  currentStopIndex: number
  destinationStopId: string | null
  stopsUntilDestination: number
  /** Index into activeItinerary.legs of the ride leg being ridden (or armed). */
  plannedLegIndex: number | null
  /** Set when the boarded vehicle's stop list doesn't contain the armed destination —
   *  the rider is on the wrong train (or a reroute). */
  wrongVehicle: boolean

  startPlannedTrip: (it: Itinerary) => void
  startRide: (vehicleId: string, vehicle: VehiclePosition, destinationStopId?: string) => void
  advanceLeg: () => void
  /** Step off the vehicle. On a planned trip the trip SURVIVES: the same leg is re-armed, so
   *  the rider can board another vehicle of that line. Only endRide abandons the plan. */
  leaveVehicle: () => void
  endRide: () => void
  setTrackedVehicle: (vehicle: VehiclePosition) => void
  setUpcomingStops: (stops: Stop[]) => void
  setDestination: (stopId: string) => void

  /** The armed/current leg of the planned trip, if any. */
  currentLeg: () => RideLeg | null
  /** The next ride leg after the current one — what the rider transfers to. */
  nextLeg: () => RideLeg | null
  /** The walk between the current ride leg and the next one, if the plan has one. */
  transferWalk: () => { minutes: number; toName: string } | null
}

const CLEARED = {
  phase: 'idle' as RidePhase,
  isRiding: false,
  trackedVehicleId: null,
  trackedVehicle: null,
  upcomingStops: [],
  currentStopIndex: 0,
  destinationStopId: null,
  stopsUntilDestination: 0,
  plannedLegIndex: null,
  wrongVehicle: false,
}

/** Drops the vehicle binding but keeps the plan: used between legs. */
const UNBOUND = {
  isRiding: false,
  trackedVehicleId: null,
  trackedVehicle: null,
  upcomingStops: [],
  currentStopIndex: 0,
  destinationStopId: null,
  stopsUntilDestination: 0,
  wrongVehicle: false,
}

export const useRiderStore = createStore<RiderState>((set, get) => ({
  ...CLEARED,

  startPlannedTrip: (it) => {
    const first = rideLegs(it)[0]
    if (first === undefined) return          // walk-only itinerary: nothing to board
    set({ ...CLEARED, phase: 'awaiting_board', plannedLegIndex: first })
  },

  startRide: (vehicleId, vehicle, destinationStopId) => {
    // The plan already knows where the rider gets off, so bind it without asking.
    const leg = get().currentLeg()
    const fromPlan = leg && sameRouteFamily(leg.routeId, vehicle.routeId)
      ? legDestinationStopId(leg)
      : null

    set({
      ...UNBOUND,
      phase: 'riding',
      isRiding: true,
      trackedVehicleId: vehicleId,
      trackedVehicle: vehicle,
      destinationStopId: destinationStopId ?? fromPlan,
      // Boarding a route the armed leg doesn't use abandons the plan — the rider changed
      // their mind, and a stale armed leg would mis-fire the transfer card.
      plannedLegIndex: fromPlan ? get().plannedLegIndex : null,
    })
  },

  advanceLeg: () => {
    const it = activeItinerary()
    const { plannedLegIndex } = get()
    if (!it || plannedLegIndex === null) { set({ ...CLEARED }); clearPlan(); return }

    const next = rideLegs(it).find((i) => i > plannedLegIndex)
    // Last leg done, so the map goes back to the whole system.
    if (next === undefined) { set({ ...CLEARED }); clearPlan(); return }

    set({ ...UNBOUND, phase: 'transfer', plannedLegIndex: next })
  },

  leaveVehicle: () => {
    // At the selected stop, stepping off completes this leg rather than re-arming the leg
    // the rider just finished. Arrival detection calls this same action, as does the X.
    if (get().plannedLegIndex !== null && hasReachedDestination(get())) {
      get().advanceLeg()
      return
    }
    // Unplanned: the vehicle WAS the session, so stepping off ends it.
    if (get().plannedLegIndex === null) { set({ ...CLEARED }); return }
    set({ ...UNBOUND, phase: 'awaiting_board' })
  },

  endRide: () => { set({ ...CLEARED }); clearPlan() },

  setTrackedVehicle: (vehicle) => {
    const { upcomingStops, destinationStopId, currentStopIndex: prevIdx } = get()

    let idx = vehicle.stopId
      ? upcomingStops.findIndex((s) => sameStop(s.stopId, vehicle.stopId))
      : -1
    if (idx < 0 && vehicle.stopSequence != null) {
      idx = upcomingStops.findIndex((s) => s.stopSequence >= vehicle.stopSequence!)
    }
    if (idx < 0) idx = 0

    // currentStopIndex means "last departed stop", so upcomingStops[i + 1] is always the
    // next one. IN_TRANSIT_TO / INCOMING_AT give the APPROACHING stop, so step back one;
    // STOPPED_AT already names the current stop.
    if ((vehicle.status === 'IN_TRANSIT_TO' || vehicle.status === 'INCOMING_AT') && idx > 0) {
      idx -= 1
    }

    // At most one stop per update, so a 2-stop feed jump still moves the panel by one.
    if (idx > prevIdx + 1) idx = prevIdx + 1

    let stopsUntilDestination = 0
    if (destinationStopId) {
      const destIdx = upcomingStops.findIndex((s) => sameStop(s.stopId, destinationStopId))
      stopsUntilDestination = destIdx >= 0 ? Math.max(0, destIdx - idx) : 0
    }

    set({ trackedVehicle: vehicle, currentStopIndex: idx, stopsUntilDestination })
  },

  setUpcomingStops: (stops) => {
    const { trackedVehicle, destinationStopId } = get()

    let currentStopIndex = trackedVehicle?.stopId
      ? stops.findIndex((s) => sameStop(s.stopId, trackedVehicle.stopId))
      : -1
    if (currentStopIndex < 0 && trackedVehicle?.stopSequence != null) {
      currentStopIndex = stops.findIndex((s) => s.stopSequence >= trackedVehicle.stopSequence!)
    }
    currentStopIndex = Math.max(0, currentStopIndex)

    // Same as setTrackedVehicle: step back in transit, so this is the last departed stop.
    if ((trackedVehicle?.status === 'IN_TRANSIT_TO' || trackedVehicle?.status === 'INCOMING_AT')
        && currentStopIndex > 0) {
      currentStopIndex -= 1
    }

    let stopsUntilDestination = 0
    let wrongVehicle = false
    if (destinationStopId) {
      const destIdx = stops.findIndex((s) => sameStop(s.stopId, destinationStopId))
      stopsUntilDestination = destIdx >= 0 ? Math.max(0, destIdx - currentStopIndex) : 0
      // Not on this vehicle's remaining path — wrong direction, wrong train, or a reroute.
      // Say so rather than count down to a stop that never comes.
      wrongVehicle = destIdx < 0 && stops.length > 0
    }

    set({ upcomingStops: stops, currentStopIndex, stopsUntilDestination, wrongVehicle })
  },

  setDestination: (stopId) => {
    // Tapping the flagged stop clears it: a toggle, and the only way off a mis-tapped
    // alight stop short of ending the ride.
    if (sameStop(get().destinationStopId, stopId)) {
      set({ destinationStopId: null, stopsUntilDestination: 0, wrongVehicle: false })
      return
    }
    const { upcomingStops, currentStopIndex } = get()
    const destIdx = upcomingStops.findIndex((s) => sameStop(s.stopId, stopId))
    if (destIdx <= currentStopIndex) return
    const stopsUntilDestination = destIdx - currentStopIndex
    set({ destinationStopId: stopId, stopsUntilDestination, wrongVehicle: false })
  },

  currentLeg: () => {
    const it = activeItinerary()
    const { plannedLegIndex } = get()
    if (!it || plannedLegIndex === null) return null
    const leg = it.legs[plannedLegIndex]
    return leg && leg.mode !== 'walk' ? leg : null
  },

  nextLeg: () => {
    const it = activeItinerary()
    const { plannedLegIndex } = get()
    if (!it || plannedLegIndex === null) return null
    const next = rideLegs(it).find((i) => i > plannedLegIndex)
    return next === undefined ? null : (it.legs[next] as RideLeg)
  },

  transferWalk: () => {
    const it = activeItinerary()
    const { plannedLegIndex, phase } = get()
    if (!it || plannedLegIndex === null) return null
    // While riding, the transfer walk is the leg AFTER the one being ridden. Once advanceLeg
    // has armed the next ride leg, plannedLegIndex has moved past it, so that same walk now
    // sits BEFORE the armed leg.
    const walk = phase === 'transfer' ? it.legs[plannedLegIndex - 1] : it.legs[plannedLegIndex + 1]
    if (!walk || walk.mode !== 'walk') return null
    return {
      minutes: Math.max(1, Math.round((walk.arrivalTime - walk.departureTime) / 60)),
      toName: walk.to.name,
    }
  },
}))
